'use client';

import * as React from 'react';
import { Button } from '@/components/ui/button';
import { ALWAYS_LIFTED, FIELD_SIZE, FieldLabel, FieldShell, fieldClasses, nodeText } from '@/components/ui/field';
import { cn, splitLayoutClasses } from '@/lib/utils';

// Built with the constructor: TypeScript rejects a `u`-flag regex literal under an ES5 target,
// which is Next 14's default.
const EMOJI = new RegExp('\\p{Extended_Pictographic}', 'gu');

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	fileName?: string;
	/**
	 * The field's name. Given one, it sits inside the control and lifts into the border when
	 * the field is focused or filled.
	 *
	 * Without one there is no notch at all: a placeholder is an example or a hint, not a
	 * name, and lifting "Search by sales rep name" into the border says something the field
	 * never claimed to be called.
	 */
	label?: React.ReactNode;
	/**
	 * Keeps the label in the notch even while the field is empty and unfocused.
	 *
	 * For a row of fields where only some carry a value: an empty one drops its label into
	 * the box and the row reads as two different controls. Pin them and the row is one
	 * control repeated. Pass a `placeholder` alongside it - the box is otherwise blank.
	 */
	pinLabel?: boolean;
}

/**
 * The product's text field. See `field.tsx` for the format and its five states.
 *
 * The label is part of the control, not a block stacked above it: pass `label` and the
 * field owns its own `<label for>`, which is why the forms in this tree no longer carry a
 * separate `<Label>`/`<FormLabel>` of their own.
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
	({ className, type, fileName, label, pinLabel, placeholder, autoComplete, onChange, onFocus, onBlur, ...props }, ref) => {
		const generatedId = React.useId();
		const id = props.id ?? generatedId;
		const [focused, setFocused] = React.useState(false);
		const [filled, setFilled] = React.useState(() => String(props.defaultValue ?? '') !== '');

		// Strip emojis. Modern Unicode pictographs only.
		const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const cleanValue = e.target.value.replace(EMOJI, '');
			e.target.value = cleanValue;
			setFilled(cleanValue !== '');
			if (onChange) onChange(e);
		};

		if (type === 'file') {
			return (
				<div className="relative">
					<input
						type="file"
						className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 disabled:cursor-not-allowed "
						ref={ref}
						onChange={onChange}
						{...props}
					/>
					<Button
						variant="outline"
						disabled={props.disabled}
						className={cn('w-full justify-center text-sm font-normal', className)}>
						{fileName ? fileName : 'Select a File'}
					</Button>
				</div>
			);
		}

		const [layout, control] = splitLayoutClasses(className);
		// The control's own left padding, mirrored onto the resting label below so the two can
		// never disagree. `FIELD_SIZE` supplies `px-3` when a call site passes nothing.
		const restingLabelInset = /(?:^|\s)(p[lx]-\S+)/.exec(control)?.[1] ?? 'pl-3';
		// The browser's own autofill list is what puts a "Your Clinic" dropdown over a field
		// the moment it is focused - it remembers anything typed into a similarly-named input
		// and covers the control with it. Ordinary product fields opt out; the ones a password
		// manager legitimately fills (sign-in, contact details) keep it, and any call site can
		// still pass its own value.
		const autoFill =
			autoComplete ?? (type === 'password' || type === 'email' || type === 'tel' ? undefined : 'off');
		// Only a real label notches. A placeholder stays a placeholder.
		const labelText = label;
		// What the box says when nothing is in it and nothing is focused.
		const restingPlaceholder = labelText ? nodeText(labelText) : placeholder;
		// A label that is a node rather than a string cannot survive the trip through the
		// `placeholder` attribute: that attribute is plain text, so `<>Name <RequiredStar /></>`
		// arrives as "Name " and the red star - the whole point of the label - is gone until
		// the field is focused. Draw the resting label as itself instead, over the empty box.
		const restingLabelNode = Boolean(labelText) && typeof labelText !== 'string';
		const value = props.value;
		const hasValue = value === undefined ? filled : String(value ?? '') !== '';
		const invalid = props['aria-invalid'] === true || props['aria-invalid'] === 'true';
		// A search box is the one field the notch does not suit: it lives in a toolbar with
		// an icon and a clear button, its placeholder is the whole instruction, and lifting
		// that instruction into a chip while the query sits below it says nothing twice.
		const lifted =
			type !== 'search' &&
			Boolean(labelText) &&
			(focused || hasValue || pinLabel || ALWAYS_LIFTED.has(type ?? ''));

		return (
			<FieldShell className={layout}>
				<input
					id={id}
					type={type}
					autoComplete={autoFill}
					// At rest a labelled field shows its label inside the box - nobody can tell
					// what an empty outline is for otherwise. Once the label has lifted into the
					// notch the box is free for the hint a call site passed alongside it.
					placeholder={lifted ? placeholder : restingLabelNode ? '' : restingPlaceholder}
					className={cn(
						fieldClasses({ lifted, invalid, disabled: props.disabled }),
						FIELD_SIZE,
						'flex',
						'ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium',
						control,
					)}
					ref={ref}
					onChange={handleTextChange}
					onFocus={(e) => {
						setFocused(true);
						onFocus?.(e);
					}}
					onBlur={(e) => {
						setFocused(false);
						setFilled(e.target.value !== '');
						onBlur?.(e);
					}}
					{...props}
					// A resting field draws its label as a <span> (below), not a <label>, so an empty
					// unfocused input had NO accessible name - which is the state first paint and a
					// screen reader both meet. The notch only becomes a real <label> once lifted.
					// Name the input from the same `label` prop in every state. DESIGN-SYSTEM §14:
					// "the placeholder is not the label … must pass a real accessible name via `label`".
					// After {...props} so an explicit caller-supplied aria-label still wins.
					aria-label={props['aria-label'] ?? (labelText ? nodeText(labelText) : undefined)}
				/>
				{lifted ? (
					<FieldLabel htmlFor={id} focused={focused} invalid={invalid} disabled={props.disabled}>
						{labelText}
					</FieldLabel>
				) : restingLabelNode ? (
					// Placeholder styling, so it reads as the placeholder it stands in for, and
					// pointer-events off so the click still lands on the input underneath.
					//
					// It starts where the input's own text starts, whatever that is. This was a
					// flat `left-7` - 28px, tuned for the one field that carries a leading icon
					// (the meeting URL box, `pl-7`) - so every other node-labelled field inherited
					// that indent and sat 16px right of the plain-string placeholder beside it,
					// then jumped back to 12px the moment anything was typed.
					<span
						className={cn(
							'pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 truncate pr-3 text-sm font-normal text-placeholder',
							restingLabelInset,
						)}>
						{labelText}
					</span>
				) : null}
			</FieldShell>
		);
	},
);
Input.displayName = 'Input';

export { Input };
